import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Pause, Play } from "lucide-react";
import { formatDurationSeconds } from "@releve/shared/format/duration";
import { useTranslation } from "@/hooks/useTranslation";

interface MemoRecorderProps {
  onRecordingComplete: (blob: Blob, durationSeconds: number, contentType: string) => void;
  disabled?: boolean;
}

type RecordingState = "idle" | "recording" | "paused" | "done";

function getSupportedMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus"))
    return "audio/webm;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  return "audio/webm";
}

export function MemoRecorder({ onRecordingComplete, disabled }: MemoRecorderProps) {
  const { t } = useTranslation("memos");
  const [state, setState] = useState<RecordingState>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const elapsedRef = useRef(0);
  const onRecordingCompleteRef = useRef(onRecordingComplete);
  onRecordingCompleteRef.current = onRecordingComplete;

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const contentType = mimeType.split(";")[0];
        const blob = new Blob(chunksRef.current, { type: contentType });
        onRecordingCompleteRef.current(blob, elapsedRef.current, contentType);
        cleanup();
        setState("done");
      };

      recorder.start(1000); // collect chunks every second
      setState("recording");
      setElapsed(0);
      elapsedRef.current = 0;

      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          elapsedRef.current = prev + 1;
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      const e = err as Error;
      if (e.name === "NotAllowedError") {
        setError(t("recorder.permissionDenied"));
      } else if (e.name === "NotFoundError") {
        setError(t("recorder.noMicrophone"));
      } else {
        setError(t("recorder.startFailed"));
      }
      cleanup();
      setState("idle");
    }
  }, [cleanup, t]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      recorderRef.current.stop();
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.pause();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setState("paused");
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === "paused") {
      recorderRef.current.resume();
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          elapsedRef.current = prev + 1;
          return prev + 1;
        });
      }, 1000);
      setState("recording");
    }
  }, []);

  const reset = useCallback(() => {
    cleanup();
    setState("idle");
    setElapsed(0);
    setError(null);
  }, [cleanup]);

  if (typeof MediaRecorder === "undefined") {
    return (
      <p className="text-sm text-destructive">
        {t("recorder.unsupported")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {state === "idle" && (
          <Button
            type="button"
            variant="outline"
            onClick={startRecording}
            disabled={disabled}
            className="gap-2"
          >
            <Mic className="h-4 w-4" />
            {t("recorder.start")}
          </Button>
        )}

        {(state === "recording" || state === "paused") && (
          <>
            {state === "recording" ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={pauseRecording}
                aria-label={t("recorder.pause")}
              >
                <Pause className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={resumeRecording}
                aria-label={t("recorder.resume")}
              >
                <Play className="h-4 w-4" />
              </Button>
            )}

            <Button
              type="button"
              variant="destructive"
              size="icon"
              onClick={stopRecording}
              aria-label={t("recorder.stop")}
            >
              <Square className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-2">
              {state === "recording" && (
                <>
                  <span
                    aria-hidden="true"
                    className="h-2.5 w-2.5 rounded-full bg-destructive animate-pulse"
                  />
                  <span className="sr-only">{t("recorder.recording")}</span>
                </>
              )}
              <span className="text-sm font-mono tabular-nums">
                {formatDurationSeconds(elapsed)}
              </span>
            </div>
          </>
        )}

        {state === "done" && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {t("recorder.saved", { duration: formatDurationSeconds(elapsed) })}
            </span>
            <Button type="button" variant="outline" size="sm" onClick={reset}>
              {t("recorder.recordAgain")}
            </Button>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
